import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useFabricationStore } from '../../stores/fabricationStore';
import { ControlType } from '../../types/persona_engine';
import { Spinner } from '../common/Spinner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';

const CockpitPanel: React.FC = () => {
  const { darkMode } = useTheme();
  const { state, isLoading, generateControls, updateControl, runCompile, auto_mode } = useFabricationStore();
  const [instruction, setInstruction] = useState('');
  
  // Add a ref for the chat container
  const chatContainerRef = useRef<HTMLDivElement>(null);
  
  // Add state for grouping controls by type
  const [groupByType, setGroupByType] = useState<boolean>(
    () => localStorage.getItem('controlGroupByType') === 'true'
  );
  
  // Persist grouping preference
  useEffect(() => {
    localStorage.setItem('controlGroupByType', groupByType.toString());
  }, [groupByType]);
  
  // Add useEffect for auto-scrolling
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [state?.conversation_cockpit]);
  
  // Memoize the sorted controls (alphabetically by label)
  const sortedControls = useMemo(() => {
    if (!state?.ui_schema?.length) return [];
    
    // Always sort alphabetically by label for consistent ordering
    return [...state.ui_schema].sort((a, b) => a.label.localeCompare(b.label));
  }, [state?.ui_schema]);
  
  // Group controls by type (memoized)
  const controlsByType = useMemo(() => {
    if (!groupByType || !sortedControls.length) return null;
    
    const grouped: Record<string, typeof sortedControls> = {};
    
    sortedControls.forEach(control => {
      const type = control.type;
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(control);
    });
    
    return grouped;
  }, [sortedControls, groupByType]);
  
  if (!state) return null;

  // Defensive coercion — older persona records or freshly-bootstrapped
  // states may lack these array fields entirely. Reading .length / .map
  // on undefined crashes the whole forge surface (operator hit this
  // 2026-05-16 on persona 321f92ae). Coerce once at the top so the
  // render code below can treat them as always-arrays.
  const uiSchema = state.ui_schema ?? [];
  const conversationCockpit = state.conversation_cockpit ?? [];

  const handleGenerateControls = async () => {
    if (!instruction.trim()) return;
    await generateControls(instruction);
    setInstruction('');
  };
  
  const handleGenerateDefaultControls = async () => {
    await generateControls(); // Call without instruction parameter
  };
  
  const renderControl = (control: any) => {
    switch (control.type) {
      case ControlType.SLIDER:
        return (
          <div className="mb-4" key={control.id}>
            <div className="flex justify-between mb-1">
              <label className="font-medium">{control.label}</label>
              <span>{typeof control.current_value === 'number' ? control.current_value.toFixed(2) : control.current_value}</span>
            </div>
            <input
              type="range"
              min={control.min || 0}
              max={control.max || 1}
              step={control.step || 0.01}
              value={control.current_value}
              onChange={(e) => updateControl(control.id, parseFloat(e.target.value))}
              className="w-full"
              disabled={state.stage_locked && state.stage !== 1}
            />
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {control.description}
            </p>
            <p className={`text-xs italic mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              {control.rationale}
            </p>
          </div>
        );
        
      case ControlType.TOGGLE:
        return (
          <div className="mb-4" key={control.id}>
            <div className="flex items-center justify-between">
              <label className="font-medium">{control.label}</label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={control.current_value}
                  onChange={(e) => updateControl(control.id, e.target.checked)}
                  className="sr-only peer"
                  disabled={state.stage_locked && state.stage !== 1}
                />
                <div className={`w-11 h-6 rounded-full peer ${
                  darkMode 
                    ? 'bg-gray-700 peer-checked:bg-blue-600' 
                    : 'bg-gray-200 peer-checked:bg-blue-600'
                } peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${(state.stage_locked && state.stage !== 1) ? 'opacity-50' : ''}`}></div>
              </label>
            </div>
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {control.description}
            </p>
            <p className={`text-xs italic mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              {control.rationale}
            </p>
          </div>
        );
        
      case ControlType.SELECT:
        return (
          <div className="mb-4" key={control.id}>
            <label className="font-medium block mb-1">{control.label}</label>
            <select
              value={control.current_value}
              onChange={(e) => updateControl(control.id, e.target.value)}
              className={`w-full p-2 rounded-md ${
                darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-800'
              } ${(state.stage_locked && state.stage !== 1) ? 'opacity-50' : ''}`}
              disabled={state.stage_locked && state.stage !== 1}
            >
              {control.options?.map((option: string) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {control.description}
            </p>
            <p className={`text-xs italic mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              {control.rationale}
            </p>
          </div>
        );
        
      case ControlType.TEXT:
        return (
          <div className="mb-4" key={control.id}>
            <label className="font-medium block mb-1">{control.label}</label>
            <input
              type="text"
              value={control.current_value}
              onChange={(e) => updateControl(control.id, e.target.value)}
              className={`w-full p-2 rounded-md ${
                darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-800'
              } ${(state.stage_locked && state.stage !== 1) ? 'opacity-50' : ''}`}
              disabled={state.stage_locked && state.stage !== 1}
            />
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {control.description}
            </p>
            <p className={`text-xs italic mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              {control.rationale}
            </p>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Tuning</h2>
      
      <div className="flex flex-col md:flex-row gap-4">
        {/* Left side - Controls */}
        <div className="md:w-1/2">
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-medium">Controls</h3>
              <div className="flex items-center space-x-2">
                {/* Always show Generate/Regenerate button */}
                <button
                  onClick={handleGenerateDefaultControls}
                  className={`text-sm px-2 py-1 rounded-md ${
                    darkMode ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'
                  } text-white ${(isLoading || (state.stage_locked && state.stage !== 1)) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={isLoading || (state.stage_locked && state.stage !== 1)}
                >
                  {isLoading ? <Spinner size="sm" /> : uiSchema.length === 0 ? 'Generate Controls' : 'Regenerate'}
                </button>
                
                {/* Show Compile button if there are controls */}
                {uiSchema.length > 0 && (
                  <button
                    onClick={() => runCompile()}
                    className={`text-sm px-2 py-1 rounded-md ${
                      darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                    } text-white ${(isLoading || (state.stage_locked && state.stage !== 1)) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={isLoading || (state.stage_locked && state.stage !== 1)}
                  >
                    {isLoading ? <Spinner size="sm" /> : 'Compile'}
                  </button>
                )}
              </div>
            </div>
            
            {uiSchema.length > 0 ? (
              <>
                <div className="mb-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">Group by Type</h3>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={groupByType}
                        onChange={(e) => setGroupByType(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className={`w-11 h-6 rounded-full peer ${
                        darkMode 
                          ? 'bg-gray-700 peer-checked:bg-blue-600' 
                          : 'bg-gray-200 peer-checked:bg-blue-600'
                      } peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all`}></div>
                    </label>
                  </div>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    When enabled, controls will be grouped by their type.
                  </p>
                </div>
                
                <div className={`p-3 rounded-md ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} flex-1 min-h-0 overflow-y-auto`}
                  aria-label="Control panel">
                  {groupByType && controlsByType ? (
                    // Render grouped by type
                    Object.entries(controlsByType).map(([type, controls]) => (
                      <div key={type} className="mb-6">
                        <h4 className={`text-sm font-semibold mb-2 pb-1 border-b ${
                          darkMode ? 'border-gray-600' : 'border-gray-300'
                        }`}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}s ({controls.length})
                        </h4>
                        <div className="space-y-4">
                          {controls.map(renderControl)}
                        </div>
                      </div>
                    ))
                  ) : (
                    // Render flat list
                    <div className="space-y-4">
                      {sortedControls.map(renderControl)}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className={`p-6 rounded-md ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} flex-1 min-h-0 flex items-center justify-center`}>
                <div className="text-center">
                  <p className="mb-4">No controls generated yet.</p>
                  <p className="text-sm text-gray-500 mb-4">Click the button below to generate controls automatically, or use the chat interface on the right for custom controls.</p>
                  
                  <button
                    onClick={handleGenerateDefaultControls}
                    className={`px-4 py-2 rounded-md ${
                      darkMode ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'
                    } text-white ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={isLoading}
                  >
                    {isLoading ? <Spinner size="sm" /> : 'Generate Controls'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Right side - Chat Interface */}
        <div className="md:w-1/2">
          <h3 className="font-medium mb-2">Control Panel Designer</h3>
          
          {/* Conversation History */}
          <div 
            ref={chatContainerRef}
            className={`p-3 mb-2 rounded-md ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} h-[300px] overflow-y-auto flex-shrink-0`}
          >
            {conversationCockpit.length > 0 ? (
              conversationCockpit.map((msg, index) => (
                <div 
                  key={msg.id || index} 
                  className={`mb-3 p-2 rounded ${
                    msg.role === 'user' 
                      ? darkMode ? 'bg-blue-900 text-blue-100' : 'bg-blue-100 text-blue-900'
                      : darkMode ? 'bg-gray-600' : 'bg-gray-200'
                  }`}
                >
                  <div className="font-semibold">{msg.role === 'user' ? 'You' : 'Assistant'}</div>
                  {msg.role === 'assistant' ? (
                    <ReactMarkdown 
                      className="prose prose-sm max-w-none dark:prose-invert text-sm"
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[
                        [(rehypeHighlight as any), { 
                          ignoreMissing: true, 
                          subset: false
                        }]
                      ]}
                      components={{
                        p: ({children}) => <p className="mb-2 text-sm">{children}</p>,
                        ul: ({children}) => <ul className="list-disc pl-5 mb-2">{children}</ul>,
                        li: ({children}) => <li className="mb-1 text-sm">{children}</li>,
                        h1: ({children}) => <h1 className="text-lg font-bold mb-2">{children}</h1>,
                        h2: ({children}) => <h2 className="text-base font-bold mb-2">{children}</h2>,
                        h3: ({children}) => <h3 className="text-sm font-bold mb-2">{children}</h3>
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                  ) : (
                    <div>{msg.content}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 h-full flex items-center justify-center">
                <div>
                  <p>No conversation yet.</p>
                  <p className="text-sm mt-2">Start by describing what controls you want for this persona.</p>
                  <p className="text-sm mt-1">For example: "Create controls for a technical writing assistant" or "Add a formality slider and a technical jargon level control"</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Chat Input — pinned below scroll area */}
          <form onSubmit={(e) => { e.preventDefault(); handleGenerateControls(); }} className="flex-shrink-0">
            <div className="flex">
              <input
                type="text"
                className={`flex-grow p-2 rounded-l-md ${
                  darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-800'
                }`}
                placeholder={uiSchema.length === 0 
                  ? "Describe controls to generate..." 
                  : "Modify controls or add new ones..."
                }
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                disabled={isLoading || (state.stage_locked && state.stage !== 1)}
              />
              <button
                type="submit"
                className={`px-4 py-2 rounded-r-md ${
                  darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                } text-white ${(isLoading || !instruction.trim() || (state.stage_locked && state.stage !== 1)) ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={isLoading || !instruction.trim() || (state.stage_locked && state.stage !== 1)}
              >
                {isLoading ? <Spinner size="sm" /> : uiSchema.length === 0 ? 'Generate' : 'Update'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CockpitPanel;
